import { createServiceContainer } from '@src/services/ServiceContainer';
import { StoryCreationService } from '@src/services/StoryCreationService';
import { UploadService } from '@src/services/upload/UploadService';
import { createDefaultUploadWorkerProvider } from '@src/services/upload/UploadWorkerProvider';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import { RootStore } from '@src/stores-v2/RootStore';

import { DI_TOKENS } from './tokens';

import type { ServiceContainer } from '@src/services/ServiceContainer';
import type { IUploadService } from '@src/services/upload/UploadService';
import type { UploadWorkerProvider } from '@src/services/upload/UploadWorkerProvider';
import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export { DI_TOKENS };

interface WindowWithStore extends Window {
  __TEST_STORE__?: IVideoCreatorStore;
}

type TokenMap = {
  [DI_TOKENS.VIDEO_CREATOR_STORE]: IVideoCreatorStore;
  [DI_TOKENS.ROOT_STORE]: RootStore;
  [DI_TOKENS.SERVICE_CONTAINER]: ServiceContainer;
  [DI_TOKENS.STORY_CREATION_SERVICE]: StoryCreationService;
  [DI_TOKENS.UPLOAD_WORKER_PROVIDER]: UploadWorkerProvider | undefined;
  [DI_TOKENS.UPLOAD_SERVICE]: IUploadService | undefined;
};

// eslint-disable-next-line no-restricted-syntax
const instances: Partial<TokenMap> = {};

function loadUploadDeps(): {
  workerProvider: UploadWorkerProvider | undefined;
  uploadService: IUploadService | undefined;
} {
  try {
    const workerProvider = createDefaultUploadWorkerProvider();
    return {
      workerProvider,
      uploadService: new UploadService(workerProvider),
    };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      'Warning: Failed to initialize upload dependencies. Uploads will not function.',
      err,
    );
    return { workerProvider: undefined, uploadService: undefined };
  }
}

/**
 * Initialize the application services. Should be called once at startup
 * (and again per fresh rootStore in tests).
 *
 * Builds (or accepts) a `ServiceContainer` holding infrastructure singletons
 * (today: `AssetPrewarmingService`). The same container instance is also
 * exposed via `<ServiceContainerProvider>` in `src/index.tsx`, so React
 * consumers and store-layer collaborators share the same services.
 */
export function initializeDIContainer(
  rootStore?: RootStore,
  serviceContainer?: ServiceContainer,
): void {
  const root = rootStore ?? new RootStore();
  const services = serviceContainer ?? createServiceContainer();
  instances[DI_TOKENS.ROOT_STORE] = root;
  instances[DI_TOKENS.SERVICE_CONTAINER] = services;

  const videoCreatorStore = new VideoCreatorStore(
    root,
    services.assetPrewarming,
  );
  root.editorStore.setPunchListStore(videoCreatorStore.punchListManager);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-restricted-syntax
    (window as WindowWithStore).__TEST_STORE__ = videoCreatorStore;
  }
  instances[DI_TOKENS.VIDEO_CREATOR_STORE] = videoCreatorStore;

  instances[DI_TOKENS.STORY_CREATION_SERVICE] = new StoryCreationService({
    videoCreator: videoCreatorStore,
    userIdentity: root.userIdentityStore,
  });

  const { workerProvider, uploadService } = loadUploadDeps();
  instances[DI_TOKENS.UPLOAD_WORKER_PROVIDER] = workerProvider;
  instances[DI_TOKENS.UPLOAD_SERVICE] = uploadService;
}

/**
 * Resolve a previously initialized service.
 */
export function resolve<T extends keyof TokenMap>(token: T): TokenMap[T] {
  const value = instances[token];
  if (value === undefined && !isOptionalToken(token)) {
    throw new Error(
      `Service "${String(token)}" was requested before initializeDIContainer() was called.`,
    );
  }
  return value as TokenMap[T];
}

function isOptionalToken(token: keyof TokenMap): boolean {
  return (
    token === DI_TOKENS.UPLOAD_WORKER_PROVIDER ||
    token === DI_TOKENS.UPLOAD_SERVICE
  );
}

/**
 * Clear all instances. Used by tests between cases.
 */
export function clearDIContainer(): void {
  (Object.keys(instances) as (keyof TokenMap)[]).forEach(k => {
    delete instances[k];
  });
}

/**
 * @deprecated Retained for backwards compatibility. Returns an object exposing
 * the current instances. New code should use `resolve(token)` directly.
 */
export function getDIContainer() {
  return instances;
}
